import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../mockData';

const SpecLine = ({ label, value }) => (
  <div className="flex gap-1">
    <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 700, color: '#FFD617' }}>
      {label}:
    </span>
    <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 400, color: '#f0f0f0' }}>
      {value}
    </span>
  </div>
);

export const Gallery = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const total = galleryImages.length;

  const prevIndex = (activeIndex - 1 + total) % total;
  const nextIndex = (activeIndex + 1) % total;

  const goPrev = useCallback(() => setActiveIndex((i) => (i - 1 + total) % total), [total]);
  const goNext = useCallback(() => setActiveIndex((i) => (i + 1) % total), [total]);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 gallery-no-img-hover">
      <div className="max-w-7xl mx-auto">

        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2
            className="font-heading mb-4 text-3xl sm:text-4xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, color: '#0a0a0a' }}
          >
            Our Work Gallery
          </h2>
          <p className="text-lg text-gray-600">
            A showcase of our precision-engineered electrical control panels
          </p>
        </div>

        {/* Carousel */}
        <div className="flex items-stretch gap-4 h-[300px] sm:h-[520px]">

          {/* Prev thumbnail — desktop only */}
          <div
            onClick={goPrev}
            className="hidden sm:block flex-shrink-0 cursor-pointer relative overflow-hidden rounded-lg"
            style={{ width: '18%' }}
          >
            <img
              src={galleryImages[prevIndex].src}
              alt={galleryImages[prevIndex].title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              style={{ display: 'block' }}
            />
            <div className="absolute inset-0 flex items-center justify-start pl-2 bg-black/40 hover:bg-black/10 transition-colors duration-300">
              <ChevronLeft className="w-8 h-8 text-white drop-shadow-lg" />
            </div>
          </div>

          {/* Active large image */}
          <div className="flex-1 relative overflow-hidden rounded-xl shadow-2xl">
            <img
              src={galleryImages[activeIndex].src}
              alt={galleryImages[activeIndex].title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              style={{ display: 'block' }}
            />

            {/* Spec overlay — desktop only */}
            <div
              className="hidden sm:block absolute bottom-0 left-0 right-0 p-6"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.55) 60%, transparent 100%)' }}
            >
              <h3
                className="mb-3"
                style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 700, color: '#FFD617' }}
              >
                {galleryImages[activeIndex].title}
              </h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <SpecLine label="Type"      value={galleryImages[activeIndex].specs.type} />
                <SpecLine label="Voltage"   value={galleryImages[activeIndex].specs.voltage} />
                <SpecLine label="Current"   value={galleryImages[activeIndex].specs.current} />
                <SpecLine label="IP Rating" value={galleryImages[activeIndex].specs.ip_rating} />
                <SpecLine label="Standard"  value={galleryImages[activeIndex].specs.standard} />
              </div>
            </div>

            {/* Nav arrows — always visible */}
            <button
              onClick={goPrev}
              aria-label="Previous panel"
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg"
              style={{ backgroundColor: 'rgba(0,0,0,0.45)', transition: 'background-color 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#004494'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.45)'; }}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={goNext}
              aria-label="Next panel"
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-2 shadow-lg"
              style={{ backgroundColor: 'rgba(0,0,0,0.45)', transition: 'background-color 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#004494'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.45)'; }}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Next thumbnail — desktop only */}
          <div
            onClick={goNext}
            className="hidden sm:block flex-shrink-0 cursor-pointer relative overflow-hidden rounded-lg"
            style={{ width: '18%' }}
          >
            <img
              src={galleryImages[nextIndex].src}
              alt={galleryImages[nextIndex].title}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              style={{ display: 'block' }}
            />
            <div className="absolute inset-0 flex items-center justify-end pr-2 bg-black/40 hover:bg-black/10 transition-colors duration-300">
              <ChevronRight className="w-8 h-8 text-white drop-shadow-lg" />
            </div>
          </div>

        </div>

        {/* Mobile spec card — hidden on sm+ */}
        <div className="sm:hidden mt-3 rounded-xl p-4" style={{ backgroundColor: '#0f0f0f' }}>
          <h3
            className="mb-2"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '15px', fontWeight: 700, color: '#FFD617' }}
          >
            {galleryImages[activeIndex].title}
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <SpecLine label="Type"      value={galleryImages[activeIndex].specs.type} />
            <SpecLine label="Voltage"   value={galleryImages[activeIndex].specs.voltage} />
            <SpecLine label="Current"   value={galleryImages[activeIndex].specs.current} />
            <SpecLine label="IP Rating" value={galleryImages[activeIndex].specs.ip_rating} />
            <SpecLine label="Standard"  value={galleryImages[activeIndex].specs.standard} />
          </div>
        </div>

        {/* Dot indicator */}
        <div className="flex justify-center mt-6 gap-1.5 flex-wrap">
          {galleryImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              aria-label={`Go to panel ${i + 1}`}
              style={{
                width: i === activeIndex ? '20px' : '8px',
                height: '8px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: i === activeIndex ? '#004494' : '#d1d5db',
                padding: 0,
              }}
            />
          ))}
        </div>

      </div>
    </section>
  );
};
